import { ActivityEvent, QueuedEvent } from './types';

/**
 * IMBEE-8954 — the durable beacon queue (TDS §6f-vi).
 *
 * WHY localStorage AND NOT AN IN-MEMORY ARRAY: on a classic multi-page site the iframe — and with
 * it every JS object the widget holds — is destroyed on each navigation. An in-memory queue would
 * lose the dwell close-out of every page a visitor ever leaves by clicking a link, which is most of
 * them. The queue lives on the WIDGET's origin, the same origin that owns `visitorToken`.
 *
 * Items are removed ONLY on a confirmed 2xx. During unload the response usually never arrives, so
 * items survive, and the next page's boot flush re-sends them; the server upserts on
 * `{sessionId, enteredAt, url, eventType}` and updates in place rather than duplicating. That is
 * why `enteredAt` is immutable once stamped — mutating it would forge a second row.
 */

export const QUEUE_KEY = 'imbee.wa.q';
export const QUEUE_VERSION = 1;
/** Oldest are dropped past this. Bounds localStorage against a visitor who never reconnects. */
export const MAX_ITEMS = 50;
/** Dwell data older than this is worthless; discarding it also bounds a stale queue. */
export const MAX_AGE_MS = 24 * 60 * 60 * 1000;

type Stored = { v: number; items: QueuedEvent[] };

export const eventId = (e: ActivityEvent): string =>
  `${e.url}|${e.enteredAt}|${e.eventType ?? 'page_view'}`;

/**
 * Every read prunes. A corrupt or foreign-versioned blob is discarded rather than repaired: this
 * is telemetry, and a parse error must never propagate into the widget's own boot path.
 */
export const readQueue = (now: number = Date.now()): QueuedEvent[] => {
  let parsed: Stored;
  try {
    const raw = window.localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    parsed = JSON.parse(raw) as Stored;
  } catch {
    return [];
  }
  if (!parsed || parsed.v !== QUEUE_VERSION || !Array.isArray(parsed.items)) return [];

  return parsed.items.filter((item) => {
    if (!item || typeof item.url !== 'string' || typeof item.enteredAt !== 'string') return false;
    const at = Date.parse(item.enteredAt);
    return Number.isFinite(at) && now - at < MAX_AGE_MS;
  });
};

const writeQueue = (items: QueuedEvent[]): void => {
  try {
    // Oldest first out, so the freshest events are the ones that survive a cap collision.
    const capped = items.slice(-MAX_ITEMS);
    window.localStorage.setItem(QUEUE_KEY, JSON.stringify({ v: QUEUE_VERSION, items: capped }));
  } catch {
    // Quota exceeded or storage disabled (private mode, blocked third-party storage). Telemetry
    // degrades to best-effort; it must never throw into the caller.
  }
};

/**
 * Upsert by id. Re-enqueuing the SAME event is the intended path for attaching `timeOnPageMs` on
 * leave, so this REPLACES rather than appends — otherwise one page produces two rows locally that
 * the server would then have to collapse.
 */
export const enqueue = (event: ActivityEvent, now: number = Date.now()): void => {
  const id = eventId(event);
  const items = readQueue(now).filter((i) => i.id !== id);
  items.push({ ...event, id });
  writeQueue(items);
};

/** The next batch, oldest first — delivery order matches visit order. */
export const takeBatch = (size: number, now: number = Date.now()): QueuedEvent[] =>
  readQueue(now).slice(0, size);

/** Called ONLY after a confirmed 2xx. */
export const removeIds = (ids: string[], now: number = Date.now()): void => {
  const gone = new Set(ids);
  writeQueue(readQueue(now).filter((i) => !gone.has(i.id)));
};

export const queueSize = (now: number = Date.now()): number => readQueue(now).length;

export const clearQueue = (): void => {
  try {
    window.localStorage.removeItem(QUEUE_KEY);
  } catch {
    /* see writeQueue */
  }
};
